import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import bodegaService from '../services/bodegaService';
import { FiCheck, FiX, FiEye } from 'react-icons/fi';
import Loading from '../components/Loading';
import './SolicitudesPage.css';

const SolicitudesBodegaPage = () => {
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSolicitud, setExpandedSolicitud] = useState(null);

  // Estados para filtros
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroRecurso, setFiltroRecurso] = useState('');
  const [filtroProyecto, setFiltroProyecto] = useState('');

  useEffect(() => {
    cargarSolicitudes();
  }, []);

  const cargarSolicitudes = async () => {
    try {
      setLoading(true);
      const response = await bodegaService.getSolicitudesBodega();
      setSolicitudes(response.data.results || response.data);
    } catch (error) {
      console.error('Error al cargar solicitudes:', error);
      toast.error('Error al cargar las solicitudes de bodega');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (solicitudId) => {
    setExpandedSolicitud(expandedSolicitud === solicitudId ? null : solicitudId);
  };

  const handleAprobar = async (solicitudId) => {
    if (!window.confirm('¿Está seguro de aprobar esta solicitud de bodega?')) {
      return;
    }

    try {
      await bodegaService.aprobarSolicitudBodega(solicitudId);
      toast.success('Solicitud de bodega aprobada exitosamente');
      cargarSolicitudes();
    } catch (error) {
      console.error('Error al aprobar solicitud:', error);
      toast.error(error.response?.data?.detail || 'Error al aprobar la solicitud');
    }
  };

  const handleRechazar = async (solicitudId) => {
    const motivo = window.prompt('Ingrese el motivo del rechazo:');
    if (!motivo) return;

    try {
      await bodegaService.rechazarSolicitudBodega(solicitudId, { observaciones_respuesta: motivo });
      toast.success('Solicitud rechazada');
      cargarSolicitudes();
    } catch (error) {
      console.error('Error al rechazar solicitud:', error);
      toast.error('Error al rechazar la solicitud');
    }
  };

  const getEstadoBadgeClass = (estado) => {
    const estadoMap = {
      'pendiente': 'badge-pendiente',
      'aprobada': 'badge-aprobada',
      'rechazada': 'badge-rechazada',
      'cancelada': 'badge-default'
    };
    return estadoMap[estado] || 'badge-default';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CL');
  };

  // Filtrar solicitudes
  const solicitudesFiltradas = solicitudes.filter(solicitud => {
    const matchEstado = !filtroEstado || solicitud.estado === filtroEstado;

    // Buscar en los detalles de la solicitud para filtrar por recurso
    const matchRecurso = !filtroRecurso || solicitud.detalles?.some(detalle =>
      detalle.recurso_nombre?.toLowerCase().includes(filtroRecurso.toLowerCase())
    );

    // Buscar en el proyecto del evento
    const matchProyecto = !filtroProyecto ||
      solicitud.tarea_evento_data?.evento_data?.nombre?.toLowerCase().includes(filtroProyecto.toLowerCase());

    return matchEstado && matchRecurso && matchProyecto;
  });

  // Calcular estadísticas por estado
  const calcularEstadisticasPorEstado = () => {
    const estados = {
      pendiente: { label: 'Pendiente', count: 0, color: '#f59e0b', bgColor: '#fef3c7' },
      aprobada: { label: 'Aprobada', count: 0, color: '#10b981', bgColor: '#d1fae5' },
      rechazada: { label: 'Rechazada', count: 0, color: '#ef4444', bgColor: '#fee2e2' },
      cancelada: { label: 'Cancelada', count: 0, color: '#6b7280', bgColor: '#f3f4f6' }
    };

    solicitudesFiltradas.forEach(solicitud => {
      if (estados[solicitud.estado]) {
        estados[solicitud.estado].count++;
      }
    });

    return estados;
  };

  const estadisticas = calcularEstadisticasPorEstado();

  if (loading) {
    return (
      <div className="solicitudes-page">
        <Loading />
      </div>
    );
  }

  return (
    <div className="solicitudes-page">
      <div className="page-header">
        <h1>Solicitudes de Bodega</h1>
        <p className="page-subtitle">
          Gestione las solicitudes de salida de recursos desde bodega para eventos
        </p>
      </div>

      {/* Estadísticas por estado */}
      <div className="stats-grid">
        {Object.entries(estadisticas).map(([key, stat]) => (
          <div
            key={key}
            className="stat-card"
            style={{ borderLeft: `4px solid ${stat.color}` }}
          >
            <div className="stat-header">
              <span className="stat-label">{stat.label}</span>
              <span
                className={`stat-badge ${getEstadoBadgeClass(key)}`}
              >
                {stat.count}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="filters-container">
        <div className="filter-group">
          <label>Estado:</label>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="filter-select"
          >
            <option value="">Todos</option>
            <option value="pendiente">Pendiente</option>
            <option value="aprobada">Aprobada</option>
            <option value="rechazada">Rechazada</option>
            <option value="cancelada">Cancelada</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Recurso:</label>
          <input
            type="text"
            value={filtroRecurso}
            onChange={(e) => setFiltroRecurso(e.target.value)}
            placeholder="Buscar recurso..."
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <label>Proyecto:</label>
          <input
            type="text"
            value={filtroProyecto}
            onChange={(e) => setFiltroProyecto(e.target.value)}
            placeholder="Buscar proyecto..."
            className="filter-input"
          />
        </div>

        {(filtroEstado || filtroRecurso || filtroProyecto) && (
          <button
            onClick={() => {
              setFiltroEstado('');
              setFiltroRecurso('');
              setFiltroProyecto('');
            }}
            className="btn-clear-filters"
          >
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Tabla de solicitudes */}
      <div className="solicitudes-table-container">
        <table className="solicitudes-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}></th>
              <th>ID</th>
              <th>Bodega</th>
              <th>Proyecto</th>
              <th>Evento/Tarea</th>
              <th>Recursos</th>
              <th>Fecha Solicitud</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {solicitudesFiltradas.length === 0 ? (
              <tr>
                <td colSpan="9" style={{ textAlign: 'center', padding: '2rem' }}>
                  No se encontraron solicitudes
                </td>
              </tr>
            ) : (
              solicitudesFiltradas.map((solicitud) => (
                <React.Fragment key={solicitud.id}>
                  <tr
                    className={`solicitud-row ${expandedSolicitud === solicitud.id ? 'expanded' : ''}`}
                  >
                    <td>
                      <button
                        className="btn-expand"
                        onClick={() => toggleExpand(solicitud.id)}
                      >
                        {expandedSolicitud === solicitud.id ? '▼' : '▶'}
                      </button>
                    </td>
                    <td>
                      <strong>#{solicitud.id}</strong>
                    </td>
                    <td>{solicitud.bodega_nombre || '-'}</td>
                    <td>{solicitud.tarea_evento_data?.evento_data?.nombre || '-'}</td>
                    <td>{solicitud.tarea_evento_titulo || '-'}</td>
                    <td>
                      <span className="cantidad-badge">
                        {solicitud.detalles?.length || 0} recurso(s)
                      </span>
                    </td>
                    <td>{formatDate(solicitud.fecha_solicitud)}</td>
                    <td>
                      <span className={`estado-badge ${getEstadoBadgeClass(solicitud.estado)}`}>
                        {solicitud.estado_display || solicitud.estado}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        {solicitud.estado === 'pendiente' && (
                          <>
                            <button
                              className="btn-action btn-aprobar"
                              onClick={() => handleAprobar(solicitud.id)}
                              title="Aprobar solicitud"
                            >
                              <FiCheck />
                            </button>
                            <button
                              className="btn-action btn-rechazar"
                              onClick={() => handleRechazar(solicitud.id)}
                              title="Rechazar solicitud"
                            >
                              <FiX />
                            </button>
                          </>
                        )}
                        <button
                          className="btn-action btn-ver"
                          onClick={() => toggleExpand(solicitud.id)}
                          title="Ver detalles"
                        >
                          <FiEye />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedSolicitud === solicitud.id && (
                    <tr className="expanded-row">
                      <td colSpan="9">
                        <div className="expanded-content">
                          <div className="details-grid">
                            <div className="detail-section">
                              <h4>Información General</h4>
                              <div className="detail-item">
                                <label>ID Solicitud:</label>
                                <span>#{solicitud.id}</span>
                              </div>
                              <div className="detail-item">
                                <label>Bodega:</label>
                                <span>{solicitud.bodega_nombre}</span>
                              </div>
                              <div className="detail-item">
                                <label>Proyecto:</label>
                                <span>{solicitud.tarea_evento_data?.evento_data?.nombre || '-'}</span>
                              </div>
                              <div className="detail-item">
                                <label>Tarea/Evento:</label>
                                <span>{solicitud.tarea_evento_titulo}</span>
                              </div>
                              <div className="detail-item">
                                <label>Solicitado por:</label>
                                <span>{solicitud.solicitado_por_nombre || '-'}</span>
                              </div>
                              <div className="detail-item">
                                <label>Fecha Solicitud:</label>
                                <span>{formatDate(solicitud.fecha_solicitud)}</span>
                              </div>
                              {solicitud.fecha_respuesta && (
                                <>
                                  <div className="detail-item">
                                    <label>Fecha Respuesta:</label>
                                    <span>{formatDate(solicitud.fecha_respuesta)}</span>
                                  </div>
                                  <div className="detail-item">
                                    <label>Respondido por:</label>
                                    <span>{solicitud.respondido_por_nombre || '-'}</span>
                                  </div>
                                </>
                              )}
                            </div>

                            <div className="detail-section">
                              <h4>Recursos Solicitados</h4>
                              {solicitud.detalles && solicitud.detalles.length > 0 ? (
                                <table className="nested-table">
                                  <thead>
                                    <tr>
                                      <th>Recurso</th>
                                      <th>Presupuesto</th>
                                      <th>Cantidad Solicitada</th>
                                      <th>Cantidad Aprobada</th>
                                      <th>Unidad</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {solicitud.detalles.map((detalle, index) => (
                                      <tr key={index}>
                                        <td><strong>{detalle.recurso_nombre}</strong></td>
                                        <td>{detalle.presupuesto_costo_recurso_nombre || '-'}</td>
                                        <td>{detalle.cantidad_solicitada}</td>
                                        <td>{detalle.cantidad_aprobada || '-'}</td>
                                        <td>{detalle.recurso_unidad}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              ) : (
                                <p>No hay recursos en esta solicitud</p>
                              )}
                            </div>
                          </div>

                          {/* Observaciones */}
                          {(solicitud.observaciones_solicitud || solicitud.observaciones_respuesta) && (
                            <div className="detail-section" style={{ marginTop: '1rem' }}>
                              <h4>Observaciones</h4>
                              {solicitud.observaciones_solicitud && (
                                <div className="detail-item">
                                  <label>Solicitud:</label>
                                  <span>{solicitud.observaciones_solicitud}</span>
                                </div>
                              )}
                              {solicitud.observaciones_respuesta && (
                                <div className="detail-item">
                                  <label>Respuesta:</label>
                                  <span>{solicitud.observaciones_respuesta}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SolicitudesBodegaPage;
